import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { PatchAdminV1StoresStoreIdPrivacyRequestsIdBody } from '@nucleus-commerce/api-client/admin';

import { customerKeys } from '@/features/customers';
import { orderKeys } from '@/features/orders';

import { privacyRequestsApi } from '../api/privacy-requests-api';

import { privacyRequestKeys } from './privacy-request-keys';

/**
 * Completing a request changes other screens: a deletion anonymises the customer and redacts
 * their orders, do_not_sell flags the customer. 409 with `meta.allowed` on a move the workflow
 * does not allow is explained by the dialog.
 */
export function useUpdatePrivacyRequest(storeId: string, successMessage: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: PatchAdminV1StoresStoreIdPrivacyRequestsIdBody;
    }) => privacyRequestsApi.update(storeId, id, body),
    meta: { successMessage, silentError: true },
    onSuccess: async (request) => {
      if (request.status === 'completed' && request.kind !== 'export') {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: customerKeys.all(storeId) }),
          queryClient.invalidateQueries({ queryKey: orderKeys.all(storeId) }),
        ]);
      }
      return queryClient.invalidateQueries({ queryKey: privacyRequestKeys.lists(storeId) });
    },
  });
}
